import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  SearchX,
  UserRound,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";
import { useTripDetail } from "@/hooks/useTripDetail";
import { formatPlanAmount, formatPlanDate } from "@/lib/builder/utils/payment-format";
import { DetailItinerariesTable } from "@/lib/dashboard/shared/DetailItinerariesTable";
import { DetailKpiTile } from "@/lib/dashboard/shared/DetailKpiTile";
import { DetailCashflowChart } from "@/lib/dashboard/shared/DetailCharts";
import { PlatformFeeDueBanner } from "@/lib/payments/components/PlatformFeeDueBanner";
import { TripStateBadge, itineraryStatusLabel } from "@/lib/dashboard/trips/trip-display";

interface TripDetailSheetProps {
  /** `null` chiude il pannello. */
  itineraryId: string | null;
  onOpenChange: (open: boolean) => void;
  /** Titolo gia' noto dalla riga: riempie l'intestazione mentre la scheda carica. */
  fallbackTitle?: string;
}

/**
 * Scheda del singolo viaggio: stessa struttura della scheda cliente -- intestazione,
 * avvisi, KPI del piano rate, rate annidate e andamento dei pagamenti.
 */
export function TripDetailSheet({ itineraryId, onOpenChange, fallbackTitle }: TripDetailSheetProps) {
  const { t } = useI18n();
  const money = useMoneyCopy();
  const navigate = useNavigate();
  const { data, loading, error, notFound, refresh } = useTripDetail(itineraryId);

  const trip = data?.trip;
  const kpis = data?.stats.kpis;
  const clientName = trip
    ? [trip.first_name, trip.last_name].filter(Boolean).join(" ") || trip.email || ""
    : "";

  return (
    <Sheet open={itineraryId !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-3xl w-full overflow-y-auto p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <SheetTitle className="text-left truncate">
                  {trip?.title ?? fallbackTitle ?? "—"}
                </SheetTitle>
                {trip && <TripStateBadge state={trip.trip_state} />}
              </div>
              <SheetDescription asChild>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-600">
                  {trip && trip.destinations.length > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      {trip.destinations.join(", ")}
                    </span>
                  )}
                  {trip?.start_date && (
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {formatPlanDate(trip.start_date)}
                      {trip.end_date ? ` – ${formatPlanDate(trip.end_date)}` : ""}
                    </span>
                  )}
                  {trip && (
                    <span className="text-gray-400">
                      {itineraryStatusLabel(trip.status, t)}
                    </span>
                  )}
                </div>
              </SheetDescription>
            </div>
            {trip && (
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0"
                onClick={() => navigate(`/itinerary-builder?itineraryId=${trip.itinerary_id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t("dashboard.overview.trips.openItinerary")}
              </Button>
            )}
          </div>

          {trip && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2 min-w-0">
                <UserAvatar
                  firstName={trip.first_name}
                  lastName={trip.last_name}
                  fullName={clientName}
                  email={trip.email}
                  imageUrl={trip.avatar_url}
                  size="sm"
                />
                <div className="flex flex-col leading-tight min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">{clientName}</span>
                  <div className="flex flex-wrap gap-x-3 text-xs">
                    {trip.email && (
                      <a
                        href={`mailto:${trip.email}`}
                        className="inline-flex items-center gap-1 text-gray-500 hover:text-orange-600"
                      >
                        <Mail className="h-3 w-3" />
                        {trip.email}
                      </a>
                    )}
                    {trip.phone && (
                      <a
                        href={`tel:${trip.phone}`}
                        className="inline-flex items-center gap-1 text-gray-500 hover:text-orange-600"
                      >
                        <Phone className="h-3 w-3" />
                        {trip.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              {trip.advisor_name && (
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                  <UserRound className="h-3.5 w-3.5" />
                  {t("dashboard.overview.trips.advisorLabel", { name: trip.advisor_name })}
                </span>
              )}
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 py-16 text-gray-500 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">{t("dashboard.overview.trips.detailLoading")}</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
                <XCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {t("dashboard.overview.errorTitle")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("dashboard.overview.errorDescription")}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => refresh()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("dashboard.retry")}
              </Button>
            </div>
          )}

          {!loading && !error && notFound && (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
              <SearchX className="h-8 w-8 text-gray-300" />
              <p className="text-sm">{t("dashboard.overview.trips.detailNotFound")}</p>
            </div>
          )}

          {!loading && !error && data && kpis && (
            <>
              <PlatformFeeDueBanner amount={kpis.platform_fee_due_amount} />

              {kpis.overdue_amount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {t("dashboard.overview.trips.overdueBanner", {
                    amount: formatPlanAmount(kpis.overdue_amount),
                  })}
                </div>
              )}

              {/* Stesso ordine della scheda cliente: pagato + in attesa = totale. */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <DetailKpiTile
                  label={t("dashboard.overview.trips.kpiInstallments")}
                  value={
                    data.trip.has_plan
                      ? `${kpis.installments_paid_count} / ${kpis.installments_count}`
                      : "—"
                  }
                  info={t("dashboard.overview.trips.kpiInstallmentsInfo")}
                />
                <DetailKpiTile
                  label={t(`${money}.paid`)}
                  value={formatPlanAmount(kpis.paid_amount)}
                  info={t("dashboard.overview.trips.kpiPaidInfo")}
                  tone="success"
                />
                <DetailKpiTile
                  label={t(`${money}.pending`)}
                  value={formatPlanAmount(kpis.remaining_amount)}
                  info={t("dashboard.overview.trips.kpiRemainingInfo")}
                  tone={kpis.overdue_amount > 0 ? "danger" : "neutral"}
                />
                <DetailKpiTile
                  label={t("dashboard.overview.trips.kpiPlanTotal")}
                  value={formatPlanAmount(kpis.plan_total)}
                  info={t("dashboard.overview.trips.kpiPlanTotalInfo")}
                  emphasis
                />
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("dashboard.overview.trips.installmentsTitle")}
                </h3>
                {data.trip.has_plan ? (
                  <DetailItinerariesTable
                    itineraries={[data.itinerary]}
                    subtitle="advisor"
                    defaultExpanded
                  />
                ) : (
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                    {t("dashboard.overview.trips.noPlan")}
                  </p>
                )}
              </div>

              {data.trip.has_plan && <DetailCashflowChart data={data.stats.cashflow} />}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default TripDetailSheet;
